import { useState, useEffect, useRef } from "react";
import { Link, useParams, useSearchParams, useNavigationType, useLocation } from "react-router-dom";
import { API } from "../App";
import Header from "../components/layout/Header";
import Footer from "../components/layout/Footer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Search, MapPin, Star, Filter, X, Utensils, Footprints, Leaf } from "lucide-react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { getLocalized } from "../utils/localized";
import { useUserLocation, distanceKm } from "../utils/useUserLocation";
import { specialtyLabelKey, filterSpecialtiesByVenueType } from "../utils/venueSpecialty";

// --- URL <-> chip state helpers ---
// Encoded format: "slug1:id1,id2|slug2:id3"
const parseChipsParam = (str) => {
  if (!str) return {};
  const result = {};
  str.split("|").forEach((group) => {
    if (!group) return;
    const idx = group.indexOf(":");
    if (idx < 0) return;
    const slug = group.slice(0, idx);
    const ids = group.slice(idx + 1).split(",").filter(Boolean);
    if (slug && ids.length) result[slug] = new Set(ids);
  });
  return result;
};
const serializeChips = (selected) => {
  const parts = [];
  Object.entries(selected || {}).forEach(([slug, set]) => {
    const ids = Array.from(set || []);
    if (ids.length) parts.push(`${slug}:${ids.join(",")}`);
  });
  return parts.join("|");
};

// sessionStorage key for scroll memory — keyed by exact path+search so each
// distinct filter combination remembers its own scroll position.
const scrollKey = (location) => `venues-scroll:${location.pathname}${location.search}`;

export default function VenueListPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { citySlug, typeSlug, cuisineSlug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigationType = useNavigationType();
  const location = useLocation();
  const scrollRestoredRef = useRef(false);
  
  const [venues, setVenues] = useState([]);
  const [cities, setCities] = useState([]);
  const [venueTypes, setVenueTypes] = useState([]);
  const [cuisines, setCuisines] = useState([]);
  const [areas, setAreas] = useState([]);
  const [dietaryTags, setDietaryTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [nearbyOnly, setNearbyOnly] = useState(false);
  const { coords } = useUserLocation();

  // URL param ?dietary_tags=slug1,slug2 → array of slugs
  const initialDietary = (searchParams.get("dietary_tags") || "").split(",").filter(Boolean);
  const [selectedDietary, setSelectedDietary] = useState(initialDietary);

  // Smart filter chips by venue_type + city
  // chipGroups: [{ group_id, name, name_el, slug, source, chips: [...] }]
  const [chipGroups, setChipGroups] = useState([]);
  // selectedChips: { [groupSlug]: Set of chip ids } — hydrate from URL so that
  // a "Back" navigation restores the user's previous filter selection.
  const [selectedChips, setSelectedChips] = useState(() => parseChipsParam(searchParams.get("chips")));

  const [filters, setFilters] = useState({
    search: searchParams.get("search") || "",
    city_id: searchParams.get("city_id") || "",
    venue_type_id: searchParams.get("venue_type_id") || "",
    cuisine_id: searchParams.get("cuisine_id") || "",
    area_id: searchParams.get("area_id") || "",
    // Sort order — wraps around to /api/venues?sort= so the backend can
    // apply the unified sort hierarchy. Defaults to relevance per spec.
    sort: searchParams.get("sort") || "relevance",
  });

  // Fetch smart chip groups whenever venue_type or city changes
  useEffect(() => {
    if (!filters.venue_type_id) {
      setChipGroups([]);
      setSelectedChips({});
      return;
    }
    const params = new URLSearchParams();
    params.set("venue_type_id", filters.venue_type_id);
    if (filters.city_id) params.set("city_id", filters.city_id);
    axios.get(`${API}/venues/filter-chips?${params.toString()}`)
      .then((res) => {
        setChipGroups(res.data || []);
        // Drop any currently-selected chips that are no longer present
        setSelectedChips((prev) => {
          const next = {};
          (res.data || []).forEach((grp) => {
            const allowed = new Set((grp.chips || []).map((c) => c.id));
            const kept = Array.from((prev[grp.slug] || new Set())).filter((id) => allowed.has(id));
            if (kept.length) next[grp.slug] = new Set(kept);
          });
          return next;
        });
      })
      .catch(() => setChipGroups([]));
  }, [filters.venue_type_id, filters.city_id]);

  const toggleChip = (groupSlug, chipId) => {
    setSelectedChips((prev) => {
      const set = new Set(prev[groupSlug] || []);
      if (set.has(chipId)) set.delete(chipId); else set.add(chipId);
      const next = { ...prev };
      if (set.size === 0) delete next[groupSlug];
      else next[groupSlug] = set;
      return next;
    });
  };

  // Fetch taxonomy data
  useEffect(() => {
    const fetchTaxonomy = async () => {
      try {
        const [citiesRes, typesRes, cuisinesRes, dietRes] = await Promise.all([
          axios.get(`${API}/taxonomy/cities`),
          axios.get(`${API}/taxonomy/venue-types`),
          axios.get(`${API}/taxonomy/cuisines`),
          axios.get(`${API}/taxonomy/dietary-tags`),
        ]);
        setCities(citiesRes.data);
        setVenueTypes(typesRes.data);
        setCuisines(cuisinesRes.data);
        setDietaryTags(dietRes.data || []);

        // Set initial filters based on URL params
        if (citySlug) {
          const city = citiesRes.data.find(c => c.slug === citySlug);
          if (city) setFilters(f => ({ ...f, city_id: city.id }));
        }
        if (typeSlug) {
          const type = typesRes.data.find(t => t.slug === typeSlug);
          if (type) setFilters(f => ({ ...f, venue_type_id: type.id }));
        }
        if (cuisineSlug) {
          const cuisine = cuisinesRes.data.find(c => c.slug === cuisineSlug);
          if (cuisine) setFilters(f => ({ ...f, cuisine_id: cuisine.id }));
        }
      } catch (error) {
        console.error("Error fetching taxonomy:", error);
      }
    };
    fetchTaxonomy();
  }, [citySlug, typeSlug, cuisineSlug]);

  // Fetch areas whenever selected city changes. Reset area_id if not valid for new city.
  useEffect(() => {
    if (!filters.city_id) {
      setAreas([]);
      if (filters.area_id) setFilters((f) => ({ ...f, area_id: "" }));
      return;
    }
    axios
      .get(`${API}/taxonomy/areas?city_id=${filters.city_id}`)
      .then((res) => setAreas(res.data || []))
      .catch(() => setAreas([]));
  }, [filters.city_id]);

  // Fetch venues
  useEffect(() => {
    // Keep URL in sync with ALL active filters so Back navigation restores state
    // and the page is shareable via deep-links.
    const next = new URLSearchParams(searchParams);
    const syncParam = (key, value) => {
      if (value) next.set(key, value);
      else next.delete(key);
    };
    syncParam("search", filters.search);
    syncParam("city_id", filters.city_id);
    syncParam("venue_type_id", filters.venue_type_id);
    syncParam("cuisine_id", filters.cuisine_id);
    syncParam("area_id", filters.area_id);
    syncParam("dietary_tags", selectedDietary.length ? selectedDietary.join(",") : "");
    syncParam("chips", serializeChips(selectedChips));
    // Only persist sort to the URL when it differs from the default so the
    // shareable URL stays clean for the common case.
    syncParam("sort", filters.sort && filters.sort !== "relevance" ? filters.sort : "");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });

    const fetchVenues = async () => {
      setLoading(true);
      try {
        const params = new URLSearchParams();
        if (filters.search) params.set("search", filters.search);
        if (filters.city_id) params.set("city_id", filters.city_id);
        if (filters.venue_type_id) params.set("venue_type_id", filters.venue_type_id);
        if (filters.area_id) params.set("area_id", filters.area_id);

        // Smart-chip selections
        const cuisineIds = [];
        const tagIds = [];
        const dietarySlugs = [];
        chipGroups.forEach((grp) => {
          const chosen = Array.from(selectedChips[grp.slug] || []);
          if (!chosen.length) return;
          if (grp.source === "cuisines") cuisineIds.push(...chosen);
          else if (grp.source === "dietary_tags") {
            // dietary chips id is the dietary_tags doc id; we need the slug
            const slugs = (grp.chips || [])
              .filter((c) => chosen.includes(c.id))
              .map((c) => c.slug);
            dietarySlugs.push(...slugs);
          } else {
            tagIds.push(...chosen);
          }
        });
        if (filters.cuisine_id && !cuisineIds.includes(filters.cuisine_id)) cuisineIds.push(filters.cuisine_id);
        if (cuisineIds.length === 1) params.set("cuisine_id", cuisineIds[0]);
        else if (cuisineIds.length > 1) params.set("cuisine_ids", cuisineIds.join(","));
        if (tagIds.length) params.set("tag_ids", tagIds.join(","));
        const allDietary = [...new Set([...selectedDietary, ...dietarySlugs])];
        if (allDietary.length) params.set("dietary_tags", allDietary.join(","));

        params.set("limit", "50");
        if (filters.sort && filters.sort !== "relevance") params.set("sort", filters.sort);

        const response = await axios.get(`${API}/venues?${params.toString()}`);
        setVenues(response.data);
      } catch (error) {
        console.error("Error fetching venues:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchVenues();
  }, [filters, selectedDietary, selectedChips, chipGroups]);

  // --- Scroll restoration ---
  // When the user clicks a venue card → detail page → clicks browser Back,
  // React Router triggers a POP navigation. We restore the previously saved
  // scroll position for this exact URL once the venue list has rendered.
  useEffect(() => {
    if (scrollRestoredRef.current) return;
    if (navigationType !== "POP") return;
    if (loading) return;
    const saved = sessionStorage.getItem(scrollKey(location));
    if (saved) {
      // Double rAF to let the DOM paint the cards before we scroll.
      requestAnimationFrame(() => requestAnimationFrame(() => {
        window.scrollTo({ top: parseInt(saved, 10) || 0, left: 0, behavior: "instant" });
      }));
    }
    scrollRestoredRef.current = true;
  }, [loading, navigationType, location]);

  // Save scroll position any time the user leaves this page (navigation,
  // refresh, tab-close). We keep one entry per unique filter URL so distinct
  // filter combinations don't stomp on each other.
  useEffect(() => {
    const save = () => {
      sessionStorage.setItem(scrollKey(location), String(window.scrollY || 0));
    };
    window.addEventListener("beforeunload", save);
    return () => {
      save();
      window.removeEventListener("beforeunload", save);
    };
  }, [location]);

  const handleFilterChange = (key, value) => {
    setFilters((f) => {
      const next = { ...f, [key]: value === "all" ? "" : value };
      // Resetting city clears the area selection
      if (key === "city_id" && next.city_id !== f.city_id) next.area_id = "";
      return next;
    });
  };

  const clearFilters = () => {
    setFilters({ search: "", city_id: "", venue_type_id: "", cuisine_id: "", area_id: "" });
    setSelectedDietary([]);
    setSelectedChips({});
    // Clear the saved scroll entry for the current filter URL so re-entering
    // doesn't jump to a stale position.
    sessionStorage.removeItem(scrollKey(location));
  };

  const toggleDietary = (slug) => {
    setSelectedDietary((prev) => prev.includes(slug) ? prev.filter(s => s !== slug) : [...prev, slug]);
  };

  const dietaryLabel = (tag) =>
    (lang === "el" && tag.name_el) || t(`dietary_tags.${tag.slug}`, { defaultValue: tag.name });

  const hasActiveFilters =
    filters.city_id || filters.venue_type_id || filters.cuisine_id || filters.area_id || filters.search ||
    selectedDietary.length > 0 || Object.keys(selectedChips).length > 0;

  // Get page title
  const getPageTitle = () => {
    if (citySlug) {
      const city = cities.find(c => c.slug === citySlug);
      return city ? `${t("venues_page:venues_in")} ${getLocalized(city, "name", lang)}` : t("venues_page:title");
    }
    if (typeSlug) {
      const type = venueTypes.find(t2 => t2.slug === typeSlug);
      return type ? getLocalized(type, "name", lang) : t("venues_page:title");
    }
    if (cuisineSlug) {
      const cuisine = cuisines.find(c => c.slug === cuisineSlug);
      return cuisine ? `${getLocalized(cuisine, "name", lang)} ${t("venues_page:cuisine")}` : t("venues_page:title");
    }
    return t("venues_page:all_venues");
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />

      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-medium text-foreground tracking-tight" data-testid="venue-list-title">
              {getPageTitle()}
            </h1>
            <p className="text-muted-foreground mt-2">
              {t("venues_page:count", { count: venues.length })}
            </p>
          </div>

          {/* Filters */}
          <div className="mb-8">
            {/* Desktop Filters: only City + Venue Type — chip groups appear below */}
            <div className="hidden md:flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("nav:search_placeholder")}
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="pl-9"
                  data-testid="venue-search-input"
                />
              </div>

              <Select value={filters.city_id || "all"} onValueChange={(v) => handleFilterChange("city_id", v)}>
                <SelectTrigger className="w-40" data-testid="venue-filter-city">
                  <SelectValue placeholder={t("venues_page:all_cities")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("venues_page:all_cities")}</SelectItem>
                  {cities.map((city) => (
                    <SelectItem key={city.id} value={city.id}>{getLocalized(city, "name", lang)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filters.venue_type_id || "all"} onValueChange={(v) => {
                handleFilterChange("venue_type_id", v);
                // Reset cuisine when venue type changes (current cuisine may not apply anymore)
                if (filters.cuisine_id) handleFilterChange("cuisine_id", "all");
              }}>
                <SelectTrigger className="w-40" data-testid="venue-filter-type">
                  <SelectValue placeholder={t("venues_page:all_types")} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t("venues_page:all_types")}</SelectItem>
                  {venueTypes.map((type) => (
                    <SelectItem key={type.id} value={type.id}>{getLocalized(type, "name", lang)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Sort By dropdown — applies the unified sort hierarchy on the
                  backend (see /api/venues `sort` param). Default: relevance. */}
              <Select value={filters.sort} onValueChange={(v) => handleFilterChange("sort", v)}>
                <SelectTrigger className="w-44" data-testid="venue-filter-sort">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="relevance">{t("venues_page:sort_relevance", { defaultValue: "Sort: Relevance" })}</SelectItem>
                  <SelectItem value="featured">{t("venues_page:sort_featured", { defaultValue: "Sort: Featured" })}</SelectItem>
                  <SelectItem value="rating">{t("venues_page:sort_rating", { defaultValue: "Sort: Best Rating" })}</SelectItem>
                  <SelectItem value="reviews">{t("venues_page:sort_reviews", { defaultValue: "Sort: Most Reviews" })}</SelectItem>
                </SelectContent>
              </Select>

              {hasActiveFilters && (
                <Button variant="ghost" size="sm" onClick={clearFilters} data-testid="venue-clear-filters">
                  <X className="h-4 w-4 mr-1" />
                  {t("venues_page:clear")}
                </Button>
              )}
            </div>

            {/* Mobile Filters Toggle */}
            <div className="md:hidden flex items-center gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t("nav:search_placeholder")}
                  value={filters.search}
                  onChange={(e) => handleFilterChange("search", e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                <Filter className="h-4 w-4 mr-2" />
                {t("venues_page:filters")}
                {hasActiveFilters && (
                  <span className="ml-2 w-5 h-5 bg-primary text-primary-foreground text-xs rounded-full flex items-center justify-center">
                    !
                  </span>
                )}
              </Button>
            </div>

            {/* Mobile Filters Panel — only City + Venue Type dropdowns */}
            {showFilters && (
              <div className="md:hidden mt-4 p-4 bg-muted rounded-lg space-y-4 animate-slide-down">
                <Select value={filters.city_id || "all"} onValueChange={(v) => handleFilterChange("city_id", v)}>
                  <SelectTrigger><SelectValue placeholder={t("venues_page:all_cities")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("venues_page:all_cities")}</SelectItem>
                    {cities.map((city) => (
                      <SelectItem key={city.id} value={city.id}>{getLocalized(city, "name", lang)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                <Select value={filters.venue_type_id || "all"} onValueChange={(v) => {
                  handleFilterChange("venue_type_id", v);
                  if (filters.cuisine_id) handleFilterChange("cuisine_id", "all");
                }}>
                  <SelectTrigger><SelectValue placeholder={t("venues_page:all_types")} /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("venues_page:all_types")}</SelectItem>
                    {venueTypes.map((type) => (
                      <SelectItem key={type.id} value={type.id}>{getLocalized(type, "name", lang)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>

                {/* Mobile Sort By */}
                <Select value={filters.sort} onValueChange={(v) => handleFilterChange("sort", v)}>
                  <SelectTrigger data-testid="venue-filter-sort-mobile"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="relevance">{t("venues_page:sort_relevance", { defaultValue: "Sort: Relevance" })}</SelectItem>
                    <SelectItem value="featured">{t("venues_page:sort_featured", { defaultValue: "Sort: Featured" })}</SelectItem>
                    <SelectItem value="rating">{t("venues_page:sort_rating", { defaultValue: "Sort: Best Rating" })}</SelectItem>
                    <SelectItem value="reviews">{t("venues_page:sort_reviews", { defaultValue: "Sort: Most Reviews" })}</SelectItem>
                  </SelectContent>
                </Select>

                {hasActiveFilters && (
                  <Button variant="outline" className="w-full" onClick={clearFilters}>
                    {t("venues_page:clear_filters")}
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* Walking-distance toggle (only when user granted geolocation) */}
          {coords && (
            <div className="mb-4" data-testid="walking-distance-row">
              <button
                type="button"
                onClick={() => setNearbyOnly((v) => !v)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-full border transition-colors ${
                  nearbyOnly
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background text-foreground border-border hover:border-primary/60"
                }`}
                data-testid="walking-distance-toggle"
              >
                <Footprints className="h-3 w-3" />
                {t("venues_page:walking_distance", { defaultValue: "Walking distance (≤ 2 km)" })}
              </button>
            </div>
          )}

          {/* Area chips (hyperlocal discovery) — visible when a city is selected and has areas */}
          {filters.city_id && areas.length > 0 && (
            <div className="mb-8 -mt-2" data-testid="area-chip-row">
              <div className="flex items-center gap-2 mb-3">
                <MapPin className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium text-muted-foreground">
                  {t("venues_page:filter_by_area", { defaultValue: "Filter by area" })}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => handleFilterChange("area_id", "all")}
                  className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                    !filters.area_id
                      ? "bg-primary text-primary-foreground border-primary"
                      : "bg-background text-foreground border-border hover:border-primary/60"
                  }`}
                  data-testid="area-chip-all"
                >
                  {t("venues_page:all_areas", { defaultValue: "All areas" })}
                </button>
                {areas.map((area) => {
                  const active = filters.area_id === area.area_id;
                  const cityLabel = getLocalized(area, "city_name", lang);
                  const areaLabel = getLocalized(area, "name", lang);
                  const label = cityLabel ? `${cityLabel} · ${areaLabel}` : areaLabel;
                  return (
                    <button
                      key={area.area_id}
                      type="button"
                      onClick={() => handleFilterChange("area_id", area.area_id)}
                      className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                        active
                          ? "bg-primary text-primary-foreground border-primary"
                          : "bg-background text-foreground border-border hover:border-primary/60"
                      }`}
                      data-testid={`area-chip-${area.area_id}`}
                    >
                      {label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Dynamic chip-group rows — only shown after a venue type is selected.
              Server returns ONLY chips with at least one matching venue, so empty
              states never appear. Each group's heading is editable from admin. */}
          {filters.venue_type_id && chipGroups.map((grp) => {
            const heading = (lang === "el" && grp.name_el) || grp.name;
            return (
              <div key={grp.group_id} className="mb-6" data-testid={`chip-group-${grp.slug}`}>
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-sm font-medium text-muted-foreground">{heading}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {grp.chips.map((chip) => {
                    const active = (selectedChips[grp.slug] || new Set()).has(chip.id);
                    const label = (lang === "el" && chip.name_el) || chip.name;
                    return (
                      <button
                        key={chip.id}
                        type="button"
                        onClick={() => toggleChip(grp.slug, chip.id)}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors ${
                          active
                            ? "bg-primary text-primary-foreground border-primary"
                            : "bg-background text-foreground border-border hover:border-primary/60"
                        }`}
                        data-testid={`chip-${grp.slug}-${chip.slug}`}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}

          {/* Venues Grid */}
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="animate-pulse">
                  <div className="bg-muted h-64 rounded-xl mb-4" />
                  <div className="bg-muted h-6 w-2/3 rounded mb-2" />
                  <div className="bg-muted h-4 w-1/2 rounded" />
                </div>
              ))}
            </div>
          ) : venues.length > 0 ? (
            (() => {
              const displayed = nearbyOnly && coords
                ? venues.filter((v) => v.latitude && v.longitude && distanceKm(coords.lat, coords.lng, v.latitude, v.longitude) <= 2)
                : venues;
              if (displayed.length === 0) {
                return (
                  <div className="text-center py-16">
                    <Footprints className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-foreground mb-2">
                      {t("venues_page:no_nearby_venues", { defaultValue: "No venues within 2 km" })}
                    </h3>
                    <Button variant="outline" onClick={() => setNearbyOnly(false)}>
                      {t("venues_page:show_all", { defaultValue: "Show all venues" })}
                    </Button>
                  </div>
                );
              }
              return (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="venue-list-grid">
                  {displayed.map((venue) => (
                    <VenueCardShared key={venue.venue_id} venue={venue} />
                  ))}
                </div>
              );
            })()
          ) : (
            <div className="text-center py-16">
              <Utensils className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">
                {t("venues_page:no_venues", { defaultValue: "No venues found" })}
              </h3>
              <p className="text-muted-foreground mb-4">
                {t("venues_page:no_venues_sub", { defaultValue: "Try adjusting your filters or search terms" })}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={clearFilters}>
                  {t("venues_page:clear_filters", { defaultValue: "Clear all filters" })}
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}

import VenueCardShared from "../components/VenueCard";
